'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import Button from 'src/components/_common/Button/Button';
import NextImage from 'src/components/_common/NextImage';
import DailyAttendanceModal from 'src/components/main/DailyAttendance/Modal';
import useRemainTimer from 'src/hooks/useRemainTimer';
import { useAppDispatch } from 'src/store/client/config';
import { modalActions } from 'src/store/client/slices/modalSlice';
import { toastActions } from 'src/store/client/slices/toastSlice';
import {
  USE_POINT_QUERY_KEY,
  useGetDailyAttendanceStatusQuery,
  useGetMyPointQuery,
  usePostDailyAttendanceMutation,
} from 'src/store/server/point/point.query';
import { checkLogin } from 'src/utils/auth';
import { momentTimezone } from 'src/utils/time';

import DailyAttendanceSnackbarStyled from './index.styled';
import SnackbarLayout from './SnackbarLayout';

const DailyAttendanceSnackbar = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { data: myPoint } = useGetMyPointQuery();
  const {
    data: dailyAttendanceStatus,
    isLoading: isDailyAttendanceStatusLoading,
    isError: isDailyAttendanceStatusError,
  } = useGetDailyAttendanceStatusQuery();

  const { mutate: postDailyAttendance } = usePostDailyAttendanceMutation({
    onSuccess: (res) => {
      queryClient.invalidateQueries([USE_POINT_QUERY_KEY.getMyPoint]);
      queryClient.invalidateQueries([USE_POINT_QUERY_KEY.getDailyAttendanceStatus]);

      // 포인트 획득 모달 표시하기
      dispatch(
        modalActions.openComponentModal({
          component: <DailyAttendanceModal point={res.data.data.point} />,
          type: 'strong',
        }),
      );
    },
    onError: () => {
      console.log('코튼 받기 실패');
      // TODO: 오류 모달 표시하기
      dispatch(
        toastActions.showToast({
          message: '포인트 지급 실패했습니다.',
        }),
      );
    },
  });

  const {
    remainTime: {
      hours: remainHours,
      minutes: remainMinutes, // minutes
      seconds: remainSeconds, // seconds
    },
    startTimer,
  } = useRemainTimer();

  const isDailyAttendanceAvailable = remainHours === '00' && remainMinutes === '00' && remainSeconds === '00';

  const handleClickButton = () => {
    if (checkLogin()) {
      postDailyAttendance();
    }

    // 로그인 페이지로 이동
    if (!checkLogin()) {
      router.push({
        pathname: '/account/login',
        query: { retUrl: router.asPath },
      });
    }
  };

  useEffect(() => {
    if (!dailyAttendanceStatus) {
      return;
    }

    try {
      const { regDttm, period } = dailyAttendanceStatus;

      if (!regDttm) {
        return;
      }

      const now = momentTimezone();
      const endTime = momentTimezone(regDttm).add(period, 'hours');

      if (endTime.isBefore(now)) {
        return;
      }

      startTimer({ endTime });
    } catch (error) {
      console.error(error);
    }
  }, [dailyAttendanceStatus]);

  if (!checkLogin() || isDailyAttendanceStatusLoading || isDailyAttendanceStatusError) {
    return null;
  }

  return (
    <SnackbarLayout>
      <DailyAttendanceSnackbarStyled.SnackbarContainer>
        <DailyAttendanceSnackbarStyled.TextContainer>
          {/* 선물 상자 이미지 */}
          {isDailyAttendanceAvailable && (
            <NextImage
              src="https://cdn.kottonseed.io/assets/main/icons/gift.webp"
              alt="선물 상자 이미지"
              width={32}
              height={32}
              aria-hidden
            />
          )}

          {/* 제목 및 포인트 표시 */}
          <DailyAttendanceSnackbarStyled.TextWrapper>
            <DailyAttendanceSnackbarStyled.Title>
              {isDailyAttendanceAvailable ? '오늘 포인트를 받아볼까요?' : '다음 포인트를 기다려주세요!'}
            </DailyAttendanceSnackbarStyled.Title>
            <DailyAttendanceSnackbarStyled.Description>
              나의 포인트 <strong>{myPoint?.point ?? '-'}</strong>
              <b>P</b>
            </DailyAttendanceSnackbarStyled.Description>
          </DailyAttendanceSnackbarStyled.TextWrapper>
        </DailyAttendanceSnackbarStyled.TextContainer>

        {/* 남은 시간 표시 */}
        {!isDailyAttendanceAvailable && (
          <DailyAttendanceSnackbarStyled.RemainTimeContainer>
            <DailyAttendanceSnackbarStyled.Description>
              <strong>남은 시간</strong>
            </DailyAttendanceSnackbarStyled.Description>
            <DailyAttendanceSnackbarStyled.RemainTimeTextContainer>
              <DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
                <DailyAttendanceSnackbarStyled.RemainTimeText>
                  {remainHours}
                </DailyAttendanceSnackbarStyled.RemainTimeText>
              </DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
              <DailyAttendanceSnackbarStyled.RemainTimeText>:</DailyAttendanceSnackbarStyled.RemainTimeText>
              <DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
                <DailyAttendanceSnackbarStyled.RemainTimeText>
                  {remainMinutes}
                </DailyAttendanceSnackbarStyled.RemainTimeText>
              </DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
              <DailyAttendanceSnackbarStyled.RemainTimeText>:</DailyAttendanceSnackbarStyled.RemainTimeText>
              <DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
                <DailyAttendanceSnackbarStyled.RemainTimeText>
                  {remainSeconds}
                </DailyAttendanceSnackbarStyled.RemainTimeText>
              </DailyAttendanceSnackbarStyled.RemainTimeTextWrapper>
            </DailyAttendanceSnackbarStyled.RemainTimeTextContainer>
          </DailyAttendanceSnackbarStyled.RemainTimeContainer>
        )}

        {/* 포인트 받기 버튼 */}
        {isDailyAttendanceAvailable && (
          <DailyAttendanceSnackbarStyled.ButtonWrapper>
            <Button colorType="purple" onClick={handleClickButton}>
              포인트 받기
            </Button>
          </DailyAttendanceSnackbarStyled.ButtonWrapper>
        )}
      </DailyAttendanceSnackbarStyled.SnackbarContainer>
    </SnackbarLayout>
  );
};

export default DailyAttendanceSnackbar;
